import { ReactNode } from 'react'
import {
	RiAppleLine,
	RiBug2Fill,
	RiCheckboxCircleLine,
	RiEdgeFill,
	RiErrorWarningLine,
	RiOperaFill,
	RiQuestionLine,
	RiServerLine,
	RiSmartphoneLine,
	RiTabletLine,
	RiWindowsLine,
	RiBuildingLine,
	RiRobot2Line,
	RiSafariLine,
	RiChromeLine,
} from 'react-icons/ri'
import { HiOutlineHome, HiOutlineDeviceMobile } from 'react-icons/hi'
import { LuLaptop } from 'react-icons/lu'
import { SiLinux } from 'react-icons/si'
import {PiWatchLight } from 'react-icons/pi'

interface Map {
	[key: string]: { icon: ReactNode; iconSm: ReactNode; label: string }
}

export const ClickStatusMapper = {
	GOOD: { bg: 'bg-green-100', color: 'text-green-700', label: 'Valid' },
	SUSPICIOUS: {
		bg: 'bg-amber-100',
		color: 'text-amber-700',
		label: 'Suspicious'
	},
	BAD: { bg: 'bg-red-100', color: 'text-red-700', label: 'Bad' },
	ORGANIC: { bg: 'bg-gray-100', color: 'text-t-secondary', label: 'Organic' },
	IGNORED: { bg: 'bg-[#374151]', color: 'text-white', label: 'Ignored' },
	WHITELSITED: {
		bg: 'bg-[#374151]',
		color: 'text-white',
		label: 'Whitelisted'
	}
}

export const ipUsageLabel = (usage: string) => {
	if (usage === 'hosting') return 'Data-center'
	if (usage === 'corporate') return 'Business'
	if (usage === 'consumer') return 'Residential'
}

export const IPUsageMapper = (usage: string | null) => {
	if (usage === 'hosting') {
		return {
			icon: <RiServerLine className="text-red-500 w-5 h-5" />,
			iconSm: <RiServerLine className="text-red-500 w-4 h-4" />,
			label: 'Data-center'
		}
	}
	if (usage === 'corporate') {
		return {
			icon: <RiBuildingLine className="text-t-desc-gray w-5 h-5" />,
			iconSm: <RiBuildingLine className="text-t-desc-gray  w-4 h-4" />,
			label: 'Business'
		}
	}
	if (usage === 'consumer') {
		return {
			icon: <HiOutlineHome className="text-t-desc-gray w-5 h-5" />,
			iconSm: <HiOutlineHome className="text-t-desc-gray w-4 h-4" />,
			label: 'Residential'
		}
	}
	return {
		icon: <RiQuestionLine className={'text-t-desc-gray w-5 h-5'} />,
		iconSm: <RiQuestionLine className={'text-t-desc-gray w-4 h-4'} />,
		label: 'Not available'
	}
}

export const ThreatLevelMapper: Map = {
	low: {
		icon: <RiCheckboxCircleLine className={'text-green-500 w-5 h-5'} />,
		iconSm: <RiCheckboxCircleLine className={'text-green-500 w-4 h-4'} />,
		label: 'Low'
	},
	medium: {
		icon: <RiErrorWarningLine className={'text-amber-500 w-5 h-5'} />,
		iconSm: <RiErrorWarningLine className={'text-amber-500 w-4 h-4'} />,
		label: 'Medium'
	},
	high: {
		icon: <RiErrorWarningLine className={'text-red-500 w-5 h-5'} />,
		iconSm: <RiErrorWarningLine className={'text-red-500 w-4 h-4'} />,
		label: 'High'
	}
}

export const DeviceTypeMapper: Map = {
	mobile: {
		icon: <HiOutlineDeviceMobile className={'w-5 h-5 text-t-desc-gray'} />,
		iconSm: <HiOutlineDeviceMobile className={'w-4 h-4 text-t-desc-gray'} />,
		label: 'Mobile'
	},
	tablet: {
		icon: <RiTabletLine className={'w-5 h-5 text-t-desc-gray'} />,
		iconSm: <RiTabletLine className={'w-4 h-4 text-t-desc-gray'} />,
		label: 'Tablet'
	},
	computer: {
		icon: <LuLaptop className={'w-5 h-5 text-t-desc-gray'} />,
		iconSm: <LuLaptop className={'w-4 h-4 text-t-desc-gray'} />,
		label: 'Computer'
	},
	wearable: {
		icon: <PiWatchLight className={'w-5 h-5 text-t-desc-gray'} />,
		iconSm: <PiWatchLight className={'w-4 h-4 text-t-desc-gray'} />,
		label: 'Wearable'
	}
}

export const DeviceOsMapper: Map = {
	Windows: {
		icon: <RiWindowsLine className="w-5 h-5 text-t-desc-gray" />,
		iconSm: <RiWindowsLine className="w-4 h-4 text-t-desc-gray" />,
		label: 'Windows'
	},
	'Mac OS': {
		icon: <RiAppleLine className="w-5 h-5 text-t-desc-gray" />,
		iconSm: <RiAppleLine className="w-4 h-4 text-t-desc-gray" />,
		label: 'Mac OS'
	},
	linux: {
		icon: <SiLinux className="w-5 h-5" />,
		iconSm: <SiLinux className="w-4 h-4" />,
		label: 'Linux'
	},
	android: {
		icon: <SiLinux className="w-5 h-5 text-t-desc-gray" />,
		iconSm: <SiLinux className="w-4 h-4 text-t-desc-gray" />,
		label: 'Android'
	},
	iOS: {
		icon: <RiAppleLine className="w-5 h-5 text-t-desc-gray" />,
		iconSm: <RiAppleLine className="w-4 h-4 text-t-desc-gray" />,
		label: 'iOS'
	}
}

export const DeviceBrowserMapper: Map = {
	Chrome: {
		icon: <RiChromeLine className={'w-5 h-5 text-t-desc-gray'} />,
		iconSm: <RiChromeLine className={'w-4 h-4 text-t-desc-gray'} />,
		label: 'Chrome'
	},
	Safari: {
		icon: <RiSafariLine className={'w-5 h-5 text-t-desc-gray'} />,
		iconSm: <RiSafariLine className={'w-4 h-4 text-t-desc-gray'} />,
		label: 'Safari'
	},
	Edge: {
		icon: <RiEdgeFill className={'w-5 h-5 text-t-desc-gray'} />,
		iconSm: <RiEdgeFill className={'w-4 h-4 text-t-desc-gray'} />,
		label: 'Edge'
	},
	Opera: {
		icon: <RiOperaFill className={'w-5 h-5 text-t-desc-gray'} />,
		iconSm: <RiOperaFill className={'w-4 h-4 text-t-desc-gray'} />,
		label: 'Opera'
	}
}

export const InteractionMapper: Map = {
	high: {
		icon: <div className="w-5 h-5 rounded-full bg-green-500" />,
		iconSm: <div className="w-[6px] h-[6px] rounded-full bg-green-500" />,
		label: 'High'
	},
	medium: {
		icon: <div className="w-5 h-5 rounded-full bg-amber-500" />,
		iconSm: <div className="w-[6px] h-[6px] rounded-full bg-amber-500" />,
		label: 'Medium'
	},
	low: {
		icon: <div className="w-5 h-5 rounded-full bg-red-500" />,
		iconSm: <div className="w-[6px] h-[6px] rounded-full bg-red-500" />,
		label: 'Low'
	},
	none: {
		icon: <div className="w-5 h-5 rounded-full bg-red-500" />,
		iconSm: <div className="w-[6px] h-[6px] rounded-full bg-red-500" />,
		label: 'None'
	}
}

export const BotProbabilityMapper: Map = {
	high: {
		icon: <RiRobot2Line className={'text-red-500 w-5 h-5'} />,
		iconSm: <RiRobot2Line className={'text-red-500 w-4 h-4'} />,
		label: 'High'
	},
	medium: {
		icon: <RiRobot2Line className={'text-amber-500 w-5 h-5'} />,
		iconSm: <RiRobot2Line className={'text-amber-500 w-4 h-4'} />,
		label: 'Medium'
	},
	low: {
		icon: <RiRobot2Line className={'text-green-500 w-5 h-5'} />,
		iconSm: <RiRobot2Line className={'text-green-500 w-4 h-4'} />,
		label: 'Low'
	}
}
